import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import {
  EdgeLabelRenderer,
  EdgeProps,
  getSmoothStepPath,
  Node,
  useNodes,
  useViewport,
} from "@xyflow/react";
import classNames from "classnames";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as styles from "../new-flow-editor.module.css";
import {
  BadgeDimensions,
  DEFAULT_BADGE_DIMENSIONS,
  measureBadgeDimensions,
} from "../utils/badge-collision";
import { useBadgePositions } from "../utils/badge-position-context";
import { findBestBadgePosition } from "../utils/smart-badge-position";
import { NodeData } from "./flow-step-node";

// Custom edge with hover labels
export const CustomFlowEdge = memo(function CustomFlowEdge({
  id,
  source,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  label,
  markerEnd,
  data,
}: EdgeProps) {
  const [isHovered, setIsHovered] = useState(false);
  const badgeRef = useRef<HTMLDivElement>(null);
  const [badgeDimensions, setBadgeDimensions] = useState<BadgeDimensions>(
    DEFAULT_BADGE_DIMENSIONS,
  );
  const [hasMeasured, setHasMeasured] = useState(false);
  const lastRegisteredRef = useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  const nodes = useNodes<Node<NodeData>>();
  const { zoom } = useViewport();
  const { getBadgePositions, registerBadgePosition, unregisterBadgePosition } =
    useBadgePositions();

  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY: sourceY - 12,
    sourcePosition,
    targetX,
    targetY: targetY + 12,
    targetPosition,
    borderRadius: 20,
  });

  const badgePosition = useMemo(() => {
    const existingBadges = getBadgePositions(id).map((info) => info.position);
    return findBestBadgePosition(
      edgePath,
      nodes,
      existingBadges,
      badgeDimensions,
    );
  }, [edgePath, nodes, badgeDimensions, getBadgePositions, id]);

  useEffect(() => {
    if (badgeRef.current && !hasMeasured) {
      const measured = measureBadgeDimensions(badgeRef.current, zoom);
      setBadgeDimensions(measured);
      setHasMeasured(true);
    }
  }, [zoom, hasMeasured, badgeDimensions, badgePosition]);

  useEffect(() => {
    if (label) {
      const lastRegistered = lastRegisteredRef.current;
      const hasChanged =
        !lastRegistered ||
        lastRegistered.x !== badgePosition.x ||
        lastRegistered.y !== badgePosition.y ||
        lastRegistered.width !== badgeDimensions.width ||
        lastRegistered.height !== badgeDimensions.height;

      if (hasChanged) {
        registerBadgePosition(id, badgePosition, badgeDimensions);
        lastRegisteredRef.current = {
          x: badgePosition.x,
          y: badgePosition.y,
          width: badgeDimensions.width,
          height: badgeDimensions.height,
        };
      }
    }

    return () => {
      unregisterBadgePosition(id);
      lastRegisteredRef.current = null;
    };
  }, [
    id,
    badgePosition,
    badgeDimensions,
    label,
    registerBadgePosition,
    unregisterBadgePosition,
  ]);

  const handleMouseEnter = useCallback((e: React.MouseEvent) => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const finalMarkerEnd = isHovered ? "url(#arrow-brand-hover)" : markerEnd;

  return (
    <>
      <svg style={{ position: "absolute", width: 0, height: 0 }}>
        <defs>
          <marker
            id="arrow-brand-hover"
            markerHeight="12.5"
            markerUnits="strokeWidth"
            markerWidth="12.5"
            orient="auto"
            refX="0"
            refY="0"
            viewBox="-10 -10 20 20"
          >
            <polyline
              fill="none"
              points="-5,-4 0,0 -5,4"
              stroke="#FF4405"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </marker>
        </defs>
      </svg>

      {/* Hover effect path */}
      {isHovered && (
        <path
          className={classNames("react-flow__edge-path", {
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            [styles.hovered]: isHovered,
          })}
          d={edgePath}
          id={`${id}-hover`}
          markerEnd={finalMarkerEnd}
          style={{
            ...style,
            stroke: "#FF4405",
            strokeWidth: (Number(style.strokeWidth) || 2) + 1,
            cursor: "pointer",
            filter: "drop-shadow(0 0 4px rgba(255, 68, 5, 0.5))",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Main path */}
      <path
        className="react-flow__edge-path"
        d={edgePath}
        id={id}
        markerEnd={finalMarkerEnd}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          ...style,
          stroke: isHovered ? "transparent" : style.stroke,
          strokeWidth: style.strokeWidth,
          cursor: "pointer",
        }}
      />

      {/* Invisible hover area */}
      <path
        d={edgePath}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          stroke: "transparent",
          strokeWidth: 20,
          fill: "none",
          cursor: "pointer",
        }}
      />

      {label && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            ref={badgeRef}
            style={{
              position: "absolute",
              left: badgePosition.x,
              top: badgePosition.y,
              transform: "translate(-50%, -50%)",
              pointerEvents: "all",
              cursor: "pointer",
              zIndex: isHovered ? 10 : 5,
            }}
          >
            <RedoBadge
              color={isHovered ? "orange" : "gray"}
              size="xs"
              text={label}
              truncateAt={20}
            />
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
});
